import { auth } from '../../firebase'

const IMPERSONATING_CLAIM = 'impersonating'

export interface RefreshOptions {
  maxAttempts?: number
  delayMs?: number
}

/**
 * Force-refresh the Firebase ID token until its `impersonating` custom claim matches
 * the expected presence, then resolve.
 *
 * Custom-claim writes (begin/end impersonation, via Admin SDK `setCustomUserClaims`)
 * are eventually consistent: a single `getIdToken(true)` immediately after the server
 * writes the claim can still return the *stale* value. If we reload on that stale
 * token the next page load sees the wrong impersonation state — most visibly, the
 * "Stop impersonating" banner never clears after a successful DELETE, so the user is
 * stuck. Polling `getIdTokenResult(true)` until the token reflects reality makes
 * start/stop deterministic.
 *
 * Best-effort: after `maxAttempts` it resolves anyway so the caller can still reload
 * (the server will issue a correct token on the next authenticated request).
 */
export async function refreshUntilImpersonation(
  expectImpersonating: boolean,
  { maxAttempts = 8, delayMs = 500 }: RefreshOptions = {},
): Promise<void> {
  const user = auth.currentUser
  if (!user) return

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await user.getIdTokenResult(true)
    const isImpersonating = Boolean(result.claims[IMPERSONATING_CLAIM])
    if (isImpersonating === expectImpersonating) return
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}
